/**
 * useLakehouseStorage Hook
 *
 * React Query hooks for managing lakehouse-storage account mappings.
 * Part of Phase 8: Lakehouse Storage Mapping.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  lakehouseStorageApi,
  LakehouseStorageMapping,
  AddStorageAccountInput,
  UpdateStorageMappingInput,
  StorageAccountPurpose,
} from '../api/lakehouseStorage';

// Query keys
export const lakehouseStorageKeys = {
  all: ['lakehouse-storage'] as const,
  lists: () => [...lakehouseStorageKeys.all, 'list'] as const,
  list: (lakehouseId: string) => [...lakehouseStorageKeys.lists(), lakehouseId] as const,
  primary: (lakehouseId: string) => [...lakehouseStorageKeys.all, 'primary', lakehouseId] as const,
};

/**
 * Hook to fetch all storage accounts for a lakehouse
 */
export function useLakehouseStorage(lakehouseId: string, enabled = true) {
  return useQuery({
    queryKey: lakehouseStorageKeys.list(lakehouseId),
    queryFn: () => lakehouseStorageApi.getStorageAccountsForLakehouse(lakehouseId),
    enabled: enabled && Boolean(lakehouseId),
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook to fetch the primary storage account for a lakehouse
 */
export function usePrimaryStorageAccount(lakehouseId: string, enabled = true) {
  return useQuery({
    queryKey: lakehouseStorageKeys.primary(lakehouseId),
    queryFn: () => lakehouseStorageApi.getPrimaryStorageAccount(lakehouseId),
    enabled: enabled && Boolean(lakehouseId),
    staleTime: 30_000,
  });
}

/**
 * Hook to add a storage account to a lakehouse
 */
export function useAddStorageAccount(lakehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddStorageAccountInput) =>
      lakehouseStorageApi.addStorageAccount(lakehouseId, input),
    onSuccess: () => {
      // Invalidate list and primary queries for this lakehouse
      queryClient.invalidateQueries({ queryKey: lakehouseStorageKeys.list(lakehouseId) });
      queryClient.invalidateQueries({ queryKey: lakehouseStorageKeys.primary(lakehouseId) });
    },
  });
}

/**
 * Hook to update a storage account mapping
 */
export function useUpdateStorageMapping(lakehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mappingId, input }: { mappingId: string; input: UpdateStorageMappingInput }) =>
      lakehouseStorageApi.updateStorageMapping(lakehouseId, mappingId, input),
    onSuccess: () => {
      // Invalidate list and primary queries
      queryClient.invalidateQueries({ queryKey: lakehouseStorageKeys.list(lakehouseId) });
      queryClient.invalidateQueries({ queryKey: lakehouseStorageKeys.primary(lakehouseId) });
    },
  });
}

/**
 * Hook to remove a storage account from a lakehouse
 */
export function useRemoveStorageAccount(lakehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mappingId, force = false }: { mappingId: string; force?: boolean }) =>
      lakehouseStorageApi.removeStorageAccount(lakehouseId, mappingId, force),
    onSuccess: () => {
      // Invalidate list and primary queries
      queryClient.invalidateQueries({ queryKey: lakehouseStorageKeys.list(lakehouseId) });
      queryClient.invalidateQueries({ queryKey: lakehouseStorageKeys.primary(lakehouseId) });
    },
  });
}

/**
 * Hook to set a storage account as primary
 */
export function useSetAsPrimary(lakehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mappingId: string) => lakehouseStorageApi.setAsPrimary(lakehouseId, mappingId),
    onSuccess: () => {
      // Invalidate list and primary queries
      queryClient.invalidateQueries({ queryKey: lakehouseStorageKeys.list(lakehouseId) });
      queryClient.invalidateQueries({ queryKey: lakehouseStorageKeys.primary(lakehouseId) });
    },
  });
}

/**
 * Hook to change the purpose of a storage account mapping
 */
export function useChangePurpose(lakehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mappingId, purpose }: { mappingId: string; purpose: StorageAccountPurpose }) =>
      lakehouseStorageApi.changePurpose(lakehouseId, mappingId, purpose),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lakehouseStorageKeys.list(lakehouseId) });
    },
  });
}

/**
 * Hook to deactivate a storage account mapping
 */
export function useDeactivateMapping(lakehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mappingId: string) =>
      lakehouseStorageApi.deactivateMapping(lakehouseId, mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lakehouseStorageKeys.list(lakehouseId) });
    },
  });
}

/**
 * Hook to reactivate a storage account mapping
 */
export function useReactivateMapping(lakehouseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mappingId: string) =>
      lakehouseStorageApi.reactivateMapping(lakehouseId, mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lakehouseStorageKeys.list(lakehouseId) });
    },
  });
}

// Re-export types for convenience
export type {
  LakehouseStorageMapping,
  AddStorageAccountInput,
  UpdateStorageMappingInput,
  StorageAccountPurpose,
  StorageMappingStatus,
} from '../api/lakehouseStorage';
